import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  StarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowTopRightOnSquareIcon,
  MapPinIcon,
  PhoneIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/solid'
import {
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconOutline,
  HandThumbUpIcon,
  HandThumbDownIcon,
  CheckCircleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline'
import { googleReviewService } from '../../services/googleReviewService'
import { settingService } from '../../services/settingService'
import { getMediaUrl } from '../../utils/media'

interface GoogleReview {
  review_id: string
  author_name: string
  author_url: string
  profile_photo_url: string
  rating: number
  relative_time_description: string
  text: string
  time: number
  language: string
  translated: boolean
  owner_response?: {
    text: string
    relative_time_description: string
    time: number
  }
}

interface ReviewSummary {
  average_rating: number
  total_reviews: number
  five_star: number
  four_star: number
  three_star: number
  two_star: number
  one_star: number
}

interface BusinessInfo {
  name: string
  place_id: string
  address: string
  phone: string
  website: string
  url: string
  photo_url: string
}

const StarRating = ({ rating, size = 'w-5 h-5' }: { rating: number; size?: string }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon
          key={star}
          className={`${size} ${star <= rating ? 'text-yellow-400' : 'text-gray-200'}`}
        />
      ))}
    </div>
  )
}

const RatingBar = ({ count, total, stars }: { count: number; total: number; stars: number }) => {
  const pct = total > 0 ? (count / total) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-gray-600 w-3">{stars}</span>
      <StarIcon className="w-4 h-4 text-yellow-400" />
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-yellow-400 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm text-gray-500 w-8 text-right">{count}</span>
    </div>
  )
}

const GoogleCredentialsSetup = ({ companyName }: { companyName?: string }) => {
  const queryClient = useQueryClient()
  const [apiKey, setApiKey] = useState('')
  const [placeId, setPlaceId] = useState('')

  const saveMutation = useMutation({
    mutationFn: () => googleReviewService.saveConfig({ apiKey, placeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-reviews-config'] })
      queryClient.invalidateQueries({ queryKey: ['google-reviews-all'] })
      queryClient.invalidateQueries({ queryKey: ['google-reviews'] })
    },
  })

  const searchQuery = encodeURIComponent(companyName || '')

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 p-8 max-w-xl mx-auto text-center">
      <div className="w-14 h-14 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <KeyIcon className="w-7 h-7 text-primary-600" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">Connect Google Reviews</h2>
      <p className="text-sm text-gray-500 mt-1 mb-6">
        Enter your Google Places API key and Place ID to pull in your real business reviews.
      </p>
      <form
        className="space-y-4 text-left"
        onSubmit={(e) => {
          e.preventDefault()
          saveMutation.mutate()
        }}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Google Places API Key</label>
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="AIza..."
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Place ID</label>
          <input
            type="text"
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            placeholder="ChIJ..."
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
          <a
            href={`https://developers.google.com/maps/documentation/places/web-service/place-id${searchQuery ? `#find-id&q=${searchQuery}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-600 hover:underline mt-1 inline-block"
          >
            Don't know your Place ID? Look it up{companyName ? ` for "${companyName}"` : ''} here
          </a>
        </div>
        {saveMutation.isError && (
          <p className="text-sm text-red-600">
            {(saveMutation.error as any)?.response?.data?.message || 'Could not verify those credentials. Please check them and try again.'}
          </p>
        )}
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Connecting...' : 'Save & Fetch Reviews'}
        </button>
      </form>
    </div>
  )
}

export const GoogleReviewsPage = () => {
  const [search, setSearch] = useState('')
  const [ratingFilter, setRatingFilter] = useState<number | ''>('')
  const [sort, setSort] = useState('newest')
  const [page, setPage] = useState(1)

  const { data: companyData } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => settingService.getCompanySettings(),
  })

  const { data: configData, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['google-reviews-config'],
    queryFn: () => googleReviewService.getConfig(),
  })

  const configured = !!configData?.data?.data?.configured

  const { data: allData, isLoading: isLoadingAll } = useQuery({
    queryKey: ['google-reviews-all'],
    queryFn: () => googleReviewService.getAllData(),
    enabled: configured,
  })

  const { data: reviewsData, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['google-reviews', ratingFilter, search, sort, page],
    queryFn: () =>
      googleReviewService.getReviews({
        rating: ratingFilter ? Number(ratingFilter) : undefined,
        search: search || undefined,
        sort,
        page,
        limit: 10,
      }),
    enabled: configured,
  })

  const business: BusinessInfo | undefined = allData?.data?.data?.business
  const summary: ReviewSummary | undefined = allData?.data?.data?.summary
  const reviews: GoogleReview[] = reviewsData?.data?.data?.items || []
  const pagination = reviewsData?.data?.data?.pagination

  const isLoading = isLoadingAll || isLoadingReviews

  const ratingOptions = [
    { label: 'All', value: '' },
    { label: '5 Stars', value: 5 },
    { label: '4 Stars', value: 4 },
    { label: '3 Stars', value: 3 },
    { label: '2 Stars', value: 2 },
    { label: '1 Star', value: 1 },
  ]

  const sortOptions = [
    { label: 'Newest First', value: 'newest' },
    { label: 'Oldest First', value: 'oldest' },
    { label: 'Highest Rated', value: 'highest' },
    { label: 'Lowest Rated', value: 'lowest' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Reviews</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and respond to your business reviews on Google
          </p>
        </div>
        {business && (
          <a
            href={business.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            View on Google Maps
          </a>
        )}
      </div>

      {isLoadingConfig ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : !configured ? (
        <GoogleCredentialsSetup companyName={companyData?.data?.data?.name} />
      ) : isLoading && !business ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <>
          {/* Business Info Card */}
          {business && (
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="h-32 bg-gradient-to-r from-primary-600 to-primary-400 relative">
                {business.photo_url && (
                  <img
                    src={getMediaUrl(business.photo_url)}
                    alt={business.name}
                    className="w-full h-full object-cover opacity-30"
                  />
                )}
                <div className="absolute inset-0 flex items-end p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white rounded-xl shadow flex items-center justify-center">
                      <MapPinIcon className="w-8 h-8 text-primary-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{business.name}</h2>
                      <div className="flex items-center gap-4 mt-1 text-white/90 text-sm">
                        <span className="flex items-center gap-1">
                          <MapPinIcon className="w-4 h-4" />
                          {business.address}
                        </span>
                        {business.phone && (
                          <span className="flex items-center gap-1">
                            <PhoneIcon className="w-4 h-4" />
                            {business.phone}
                          </span>
                        )}
                        {business.website && <a
                          href={business.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:underline"
                        >
                          <GlobeAltIcon className="w-4 h-4" />
                          Website
                        </a>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats & Summary */}
          {summary && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Average Rating Card */}
              <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-gray-900">{summary.average_rating.toFixed(1)}</p>
                    <StarRating rating={Math.round(summary.average_rating)} size="w-5 h-5" />
                    <p className="text-sm text-gray-500 mt-1">{summary.total_reviews} reviews</p>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <RatingBar count={summary.five_star} total={summary.total_reviews} stars={5} />
                    <RatingBar count={summary.four_star} total={summary.total_reviews} stars={4} />
                    <RatingBar count={summary.three_star} total={summary.total_reviews} stars={3} />
                    <RatingBar count={summary.two_star} total={summary.total_reviews} stars={2} />
                    <RatingBar count={summary.one_star} total={summary.total_reviews} stars={1} />
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow border border-gray-100 p-5 flex flex-col items-center justify-center text-center">
                  <div className="p-3 bg-green-50 rounded-full mb-2">
                    <HandThumbUpIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.five_star + summary.four_star}
                  </p>
                  <p className="text-sm text-gray-500">Positive</p>
                </div>
                <div className="bg-white rounded-xl shadow border border-gray-100 p-5 flex flex-col items-center justify-center text-center">
                  <div className="p-3 bg-yellow-50 rounded-full mb-2">
                    <ChatBubbleLeftRightIconOutline className="w-6 h-6 text-yellow-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {reviews.filter((r) => r.owner_response).length}
                  </p>
                  <p className="text-sm text-gray-500">Responded</p>
                </div>
                <div className="bg-white rounded-xl shadow border border-gray-100 p-5 flex flex-col items-center justify-center text-center">
                  <div className="p-3 bg-blue-50 rounded-full mb-2">
                    <CheckCircleIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(((summary.five_star + summary.four_star) / summary.total_reviews) * 100)}%
                  </p>
                  <p className="text-sm text-gray-500">Satisfaction</p>
                </div>
                <div className="bg-white rounded-xl shadow border border-gray-100 p-5 flex flex-col items-center justify-center text-center">
                  <div className="p-3 bg-red-50 rounded-full mb-2">
                    <HandThumbDownIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.two_star + summary.one_star}
                  </p>
                  <p className="text-sm text-gray-500">Negative</p>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search reviews by keyword or author..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(1)
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <FunnelIcon className="w-5 h-5 text-gray-400" />
                <select
                  value={ratingFilter}
                  onChange={(e) => {
                    setRatingFilter(e.target.value === '' ? '' : Number(e.target.value))
                    setPage(1)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                >
                  {ratingOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <select
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value)
                    setPage(1)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Reviews List */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
              <ChatBubbleLeftRightIconOutline className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No reviews found</h3>
              <p className="text-gray-500">
                {search || ratingFilter
                  ? 'Try adjusting your filters'
                  : 'No Google reviews available at the moment'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div
                  key={review.review_id}
                  className="bg-white rounded-xl shadow border border-gray-100 p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={review.profile_photo_url}
                        alt={review.author_name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{review.author_name}</p>
                        <p className="text-xs text-gray-500">{review.relative_time_description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg">
                      <StarIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-bold text-yellow-700">{review.rating}.0</span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-gray-700 text-sm leading-relaxed">{review.text}</p>
                  </div>

                  {review.owner_response && (
                    <div className="mt-4 ml-4 pl-4 border-l-4 border-primary-200 bg-primary-50/50 rounded-r-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-primary-700">Owner Response</span>
                        <span className="text-xs text-gray-500">
                          {review.owner_response.relative_time_description}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{review.owner_response.text}</p>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-4">
                    <button className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors">
                      <HandThumbUpIcon className="w-4 h-4" />
                      Helpful
                    </button>
                    <button className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors">
                      <ChatBubbleLeftRightIconOutline className="w-4 h-4" />
                      Respond
                    </button>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 p-4">
                  <p className="text-sm text-gray-500">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} reviews
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                      disabled={page === pagination.pages}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
